import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ShieldCheck, ChevronRight, ChevronLeft, Check, Lock, UploadCloud, FileText, X,
  CircleCheck, CircleDashed, PartyPopper, Plus,
} from "lucide-react";

const FRAME = "bg-white rounded-2xl shadow-xl ring-1 ring-black/5";

// ---- conditional visibility (mirrors backend onboarding_defaults._visible) ----
function isVisible(q, answers) {
  const c = q.show_if;
  if (!c) return true;
  const val = answers[c.key];
  const t = c.value;
  switch (c.op) {
    case "ne": return String(val) !== String(t);
    case "in": return Array.isArray(t) && t.includes(val);
    case "contains": return !!val && String(val).includes(t);
    default: return String(val) === String(t);
  }
}

function isAnswered(q, answers, documents) {
  if (q.document) return !!documents[q.key];
  const v = answers[q.key];
  if (q.field_type === "people") return Array.isArray(v) && v.some((p) => p && p.name);
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && v !== "";
}

export default function OnboardingForm() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState(null);
  const [answers, setAnswers] = useState({});
  const [documents, setDocuments] = useState({});
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const [confirmCorrect, setConfirmCorrect] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/onboarding/${token}`).then((r) => {
      const d = r.data;
      setForm(d);
      setAnswers(d.answers || {});
      setDocuments(d.documents || {});
      setConfirmCorrect(!!d.confirm_correct);
      setSubmitted(!!d.submitted_at);
    }).catch((e) => setErr(apiError(e.response?.data?.detail) || "Onboarding is not available yet."));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const sections = form?.sections || [];
  const questions = form?.questions || [];
  const section = sections[idx];

  const bySection = useMemo(() => {
    const map = {};
    for (const q of questions) (map[q.section] ||= []).push(q);
    return map;
  }, [questions]);

  // Section completeness (visible required all answered)
  const sectionComplete = useCallback((secKey) => {
    const qs = (bySection[secKey] || []).filter((q) => isVisible(q, answers));
    const req = qs.filter((q) => q.required);
    return req.every((q) => isAnswered(q, answers, documents));
  }, [bySection, answers, documents]);

  const contentSections = sections.filter((s) => s.key !== "review");
  const completeCount = contentSections.filter((s) => sectionComplete(s.key)).length;
  const missing = useMemo(
    () => questions.filter((q) => q.required && isVisible(q, answers) && !isAnswered(q, answers, documents)),
    [questions, answers, documents],
  );

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const saveAnswers = async (silent = false) => {
    setSaving(true);
    try {
      await api.put(`/public/onboarding/${token}`, { answers });
      if (!silent) toast.success("Saved");
      return true;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not save");
      return false;
    } finally { setSaving(false); }
  };

  const goNext = async () => { await saveAnswers(true); setIdx((i) => Math.min(i + 1, sections.length - 1)); window.scrollTo(0, 0); };
  const goPrev = () => { setIdx((i) => Math.max(i - 1, 0)); window.scrollTo(0, 0); };
  const saveExit = async () => { if (await saveAnswers()) toast.success("Progress saved — come back any time using your link."); };

  const uploadFile = async (q, file) => {
    if (!file) return;
    setUploading(q.key);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/public/onboarding/${token}/upload?field=${q.key}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setDocuments((d) => ({ ...d, [q.key]: r.data.document }));
      toast.success("File uploaded");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(""); }
  };

  const removeFile = async (q) => {
    try {
      await api.delete(`/public/onboarding/${token}/upload?field=${q.key}`);
      setDocuments((d) => { const n = { ...d }; delete n[q.key]; return n; });
    } catch { /* noop */ }
  };

  const submit = async () => {
    if (!confirmCorrect) { toast.error("Please confirm the information is correct"); return; }
    if (missing.length) { setShowErrors(true); toast.error("Some required information is still missing"); return; }
    setSaving(true);
    try {
      await api.post(`/public/onboarding/${token}/submit`, { answers, confirm_correct: true });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.missing) { setShowErrors(true); toast.error("Some required information is still missing"); }
      else toast.error(apiError(det) || "Could not submit");
    } finally { setSaving(false); }
  };

  const Header = (
    <div className="fixed top-0 inset-x-0 z-30 bg-white/85 backdrop-blur-xl border-b border-border">
      <div className="max-w-[960px] mx-auto flex items-center justify-between px-6 py-3">
        <div className="font-alt font-extrabold text-cass-navy text-lg">CASS</div>
        <div className="text-sm text-cass-slate">{form?.client_name}</div>
      </div>
    </div>
  );

  if (err) return <div className="min-h-screen grid place-items-center text-cass-slate px-6 text-center" data-testid="onboarding-error">{err}</div>;
  if (!form) return <div className="min-h-screen grid place-items-center text-cass-slate">Loading your onboarding…</div>;

  if (submitted) {
    return (
      <div className="bg-cass-bone min-h-screen">
        {Header}
        <div className="pt-24 pb-20 px-4">
          <div className={`max-w-[720px] mx-auto ${FRAME} p-10 text-center`} data-testid="onboarding-submitted">
            <PartyPopper className="w-12 h-12 text-cass-teal mx-auto mb-4" />
            <h1 className="font-display text-3xl md:text-4xl font-bold text-cass-navy mb-3">Thank you — all done!</h1>
            <p className="text-cass-slate text-lg leading-relaxed max-w-md mx-auto">
              We've received your onboarding details for <span className="font-semibold text-cass-navy">{form.client_name}</span>.
              Our team will review everything and be in touch shortly to complete your setup.
            </p>
            <Button className="mt-8 bg-cass-teal text-white rounded-full px-8 h-12"
              onClick={() => { window.location.href = "https://www.cassonline.co.uk"; }} data-testid="onboarding-done-cta">
              Back to cassonline.co.uk<ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isReview = section?.key === "review";

  return (
    <div className="bg-cass-bone min-h-screen">
      {Header}
      <div className="pt-24 pb-32 px-4">
        <div className="max-w-[960px] mx-auto grid md:grid-cols-[240px_1fr] gap-6">

          {/* Left: section nav + progress */}
          <aside className="md:sticky md:top-24 self-start" data-testid="onboarding-progress">
            <div className={`${FRAME} p-5`}>
              <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-1">Your onboarding</div>
              <div className="text-cass-navy font-display text-lg font-bold mb-1">
                {completeCount} of {contentSections.length} complete
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-cass-teal transition-all" style={{ width: `${(completeCount / Math.max(1, contentSections.length)) * 100}%` }} />
              </div>
              <nav className="space-y-1">
                {sections.map((s, i) => {
                  const done = s.key !== "review" && sectionComplete(s.key);
                  const active = i === idx;
                  return (
                    <button key={s.key} type="button" onClick={() => { setIdx(i); window.scrollTo(0, 0); }}
                      className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-cass-navy text-white" : "text-cass-slate hover:bg-slate-50"}`}
                      data-testid={`onboarding-nav-${s.key}`}>
                      {done ? <CircleCheck className="w-4 h-4 text-cass-teal shrink-0" /> : <CircleDashed className="w-4 h-4 shrink-0 opacity-60" />}
                      <span className="truncate">{s.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Right: current section */}
          <motion.main key={section?.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <div className={`${FRAME} p-6 md:p-8`} data-testid="onboarding-section">
              <div className="text-cass-teal text-xs font-semibold uppercase tracking-widest mb-1">Step {idx + 1} of {sections.length}</div>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-cass-navy mb-6">{section?.title}</h1>

              {!isReview && (
                <div className="space-y-5">
                  {(bySection[section?.key] || []).filter((q) => isVisible(q, answers)).map((q) => (
                    <Field key={q.key} q={q} value={answers[q.key]} doc={documents[q.key]}
                      onChange={(v) => setAnswer(q.key, v)} onUpload={(f) => uploadFile(q, f)}
                      onRemove={() => removeFile(q)} uploading={uploading === q.key}
                      invalid={showErrors && q.required && !isAnswered(q, answers, documents)} />
                  ))}
                  {(bySection[section?.key] || []).filter((q) => isVisible(q, answers)).length === 0 && (
                    <p className="text-cass-slate text-sm">Nothing to complete in this section — you can continue.</p>
                  )}
                </div>
              )}

              {isReview && (
                <ReviewPanel sections={contentSections} bySection={bySection} answers={answers} documents={documents}
                  sectionComplete={sectionComplete} missing={missing} onJump={(sKey) => setIdx(sections.findIndex((s) => s.key === sKey))}
                  confirmCorrect={confirmCorrect} setConfirmCorrect={setConfirmCorrect} />
              )}

              {/* nav buttons */}
              <div className="mt-8 flex items-center justify-between gap-3 border-t border-border pt-5">
                <Button variant="ghost" className="text-cass-slate" disabled={idx === 0} onClick={goPrev} data-testid="onboarding-back">
                  <ChevronLeft className="w-4 h-4 mr-1" />Back
                </Button>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={saveExit} disabled={saving} data-testid="onboarding-save-exit">Save &amp; exit</Button>
                  {!isReview ? (
                    <Button className="bg-cass-teal text-white rounded-full px-6" onClick={goNext} disabled={saving} data-testid="onboarding-next">
                      Save &amp; continue<ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button className="bg-cass-navy text-white rounded-full px-6" onClick={submit} disabled={saving || !confirmCorrect} data-testid="onboarding-submit">
                      <Check className="w-4 h-4 mr-1" />Submit onboarding
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.main>
        </div>
      </div>
    </div>
  );
}

// -------------------------- Field renderer --------------------------
function Field({ q, value, doc, onChange, onUpload, onRemove, uploading, invalid }) {
  const baseLabel = (
    <Label className="text-cass-navy font-semibold flex items-center gap-1.5">
      {q.label}{q.required && <span className="text-cass-teal">*</span>}
      {q.sensitive && <Lock className="w-3 h-3 text-cass-slate" title="Stored securely" />}
    </Label>
  );
  const help = q.help ? <p className="text-xs text-cass-slate mt-1">{q.help}</p> : null;
  const ring = invalid ? "border-red-400 ring-1 ring-red-300" : "";
  const tid = `onb-${q.key}`;

  if (q.document) {
    return (
      <div data-testid={`field-${q.key}`}>
        {baseLabel}
        {help}
        {doc ? (
          <div className="mt-2 flex items-center justify-between gap-3 bg-cass-bone rounded-lg px-3 py-2.5" data-testid={`${tid}-file`}>
            <span className="flex items-center gap-2 text-sm text-cass-navy truncate"><FileText className="w-4 h-4 text-cass-teal shrink-0" />{doc.filename}</span>
            <button type="button" onClick={onRemove} className="text-cass-slate hover:text-red-500" data-testid={`${tid}-remove`}><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <label className={`mt-2 flex items-center gap-2 justify-center border-2 border-dashed rounded-lg px-4 py-4 cursor-pointer text-sm text-cass-slate hover:border-cass-teal ${invalid ? "border-red-400" : "border-border"}`} data-testid={`${tid}-drop`}>
            <UploadCloud className="w-4 h-4" />{uploading ? "Uploading…" : "Choose a file (PDF, image, Word, Excel)"}
            <input type="file" className="hidden" disabled={uploading}
              accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv"
              onChange={(e) => onUpload(e.target.files?.[0])} data-testid={`${tid}-input`} />
          </label>
        )}
      </div>
    );
  }

  if (q.field_type === "yes_no") {
    return (
      <div data-testid={`field-${q.key}`}>
        {baseLabel}{help}
        <div className="mt-2 flex gap-2">
          {[["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => onChange(v)}
              className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors ${String(value) === v ? "bg-cass-teal text-white border-cass-teal" : `bg-white text-cass-navy ${invalid ? "border-red-400" : "border-border"} hover:border-cass-navy`}`}
              data-testid={`${tid}-${v}`}>{l}</button>
          ))}
        </div>
      </div>
    );
  }

  if (q.field_type === "people") {
    const arr = Array.isArray(value) ? value : [];
    const SUB = [["name", "Full name", "text"], ["dob", "Date of birth", "text"], ["utr", "UTR", "password"], ["ni_number", "National Insurance no.", "password"]];
    const update = (i, k, v) => onChange(arr.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
    return (
      <div data-testid={`field-${q.key}`}>
        {baseLabel}{help}
        <div className="mt-2 space-y-3">
          {arr.length === 0 && <p className="text-sm text-cass-slate">No individuals added yet.</p>}
          {arr.map((p, i) => (
            <div key={i} className="rounded-lg border border-border p-3 bg-cass-bone/40" data-testid={`${tid}-person-${i}`}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-cass-navy">Individual {i + 1}</span>
                <button type="button" onClick={() => onChange(arr.filter((_, j) => j !== i))} className="text-cass-slate hover:text-red-500" data-testid={`${tid}-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {SUB.map(([k, l, t]) => (
                  <div key={k}>
                    <label className="text-xs text-cass-slate flex items-center gap-1">{l}{(k === "utr" || k === "ni_number") && <Lock className="w-3 h-3" />}</label>
                    <Input type={t} value={p[k] || ""} onChange={(e) => update(i, k, e.target.value)} className="mt-1 h-9" placeholder={k === "dob" ? "dd/mm/yyyy" : ""} autoComplete="off" data-testid={`${tid}-${i}-${k}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => onChange([...arr, {}])} data-testid={`${tid}-add`}><Plus className="w-4 h-4 mr-1" />Add another individual</Button>
        </div>
      </div>
    );
  }

  if (q.field_type === "checkbox") {
    return (
      <div data-testid={`field-${q.key}`}>
        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox checked={value === "yes" || value === true} onCheckedChange={(v) => onChange(v ? "yes" : "")} className="mt-0.5" data-testid={`${tid}-checkbox`} />
          <span className="text-sm text-cass-navy">{q.label}{q.required && <span className="text-cass-teal"> *</span>}</span>
        </label>
        {help}
      </div>
    );
  }

  if (q.field_type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    return (
      <div data-testid={`field-${q.key}`}>
        {baseLabel}{help}
        <div className="mt-2 flex flex-wrap gap-2">
          {q.options.map(([v, l]) => (
            <button key={v} type="button" onClick={() => toggle(v)}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${arr.includes(v) ? "bg-cass-teal text-white border-cass-teal" : `bg-white text-cass-navy ${invalid ? "border-red-400" : "border-border"} hover:border-cass-navy`}`}
              data-testid={`${tid}-${v}`}>{l}</button>
          ))}
        </div>
      </div>
    );
  }

  if (q.field_type === "select") {
    return (
      <div data-testid={`field-${q.key}`}>
        {baseLabel}{help}
        <select value={value || ""} onChange={(e) => onChange(e.target.value)}
          className={`mt-1.5 w-full h-10 rounded-md border border-border bg-white px-3 text-sm text-cass-navy ${ring}`} data-testid={`${tid}-select`}>
          <option value="">Please choose…</option>
          {q.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    );
  }

  if (q.field_type === "textarea" || q.field_type === "address") {
    return (
      <div data-testid={`field-${q.key}`}>
        {baseLabel}{help}
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
          className={`mt-1.5 w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-cass-navy ${ring}`} data-testid={`${tid}-input`} />
      </div>
    );
  }

  const type = q.field_type === "secure_text" ? "password"
    : q.field_type === "number" || q.field_type === "currency" ? "number"
    : q.field_type === "email" ? "email"
    : q.field_type === "phone" ? "tel"
    : q.field_type === "date" ? "text"
    : "text";
  return (
    <div data-testid={`field-${q.key}`}>
      {baseLabel}{help}
      <div className="relative mt-1.5">
        {q.field_type === "currency" && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-cass-slate text-sm">£</span>}
        <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
          placeholder={q.field_type === "date" ? "dd/mm/yyyy" : q.placeholder}
          className={`${q.field_type === "currency" ? "pl-7" : ""} ${ring}`} data-testid={`${tid}-input`} autoComplete={q.sensitive ? "off" : undefined} />
      </div>
    </div>
  );
}

// -------------------------- Review panel --------------------------
function ReviewPanel({ sections, bySection, answers, documents, sectionComplete, missing, onJump, confirmCorrect, setConfirmCorrect }) {
  return (
    <div className="space-y-4" data-testid="onboarding-review">
      <p className="text-cass-slate">Please check each section below. Anything missing is highlighted — tap a section to complete it.</p>
      {sections.map((s) => {
        const done = sectionComplete(s.key);
        const qs = (bySection[s.key] || []).filter((q) => isVisible(q, answers));
        return (
          <div key={s.key} className="border border-border rounded-xl p-4" data-testid={`review-${s.key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {done ? <CircleCheck className="w-5 h-5 text-cass-teal" /> : <CircleDashed className="w-5 h-5 text-amber-500" />}
                <span className="font-semibold text-cass-navy">{s.title}</span>
                {!done && <span className="text-xs text-amber-600">Needs attention</span>}
              </div>
              <button type="button" onClick={() => onJump(s.key)} className="text-xs text-cass-teal underline" data-testid={`review-edit-${s.key}`}>Edit</button>
            </div>
            <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {qs.map((q) => {
                const answered = isAnswered(q, answers, documents);
                const raw = answers[q.key];
                const shown = q.document ? (documents[q.key]?.filename || "")
                  : q.sensitive && raw ? "••••••"
                  : q.field_type === "multiselect" && Array.isArray(raw) ? raw.map((v) => q.options.find((o) => o[0] === v)?.[1] || v).join(", ")
                  : q.field_type === "checkbox" ? (raw === "yes" || raw === true ? "Yes" : "")
                  : q.field_type === "people" && Array.isArray(raw) ? raw.map((p) => p.name).filter(Boolean).join(", ")
                  : q.field_type === "select" ? (q.options.find((o) => o[0] === raw)?.[1] || raw)
                  : raw;
                return (
                  <div key={q.key} className="flex justify-between gap-3 py-0.5">
                    <span className="text-cass-slate truncate">{q.label}{q.required && !answered && <span className="text-amber-600"> *</span>}</span>
                    <span className={`font-medium truncate ${answered ? "text-cass-navy" : "text-amber-600"}`}>{answered ? String(shown) : "—"}</span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {missing.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800" data-testid="onboarding-missing">
          {missing.length} required {missing.length === 1 ? "item is" : "items are"} still missing before you can submit.
        </div>
      )}

      <label className="flex items-start gap-2 cursor-pointer bg-cass-bone rounded-lg p-4 mt-2">
        <Checkbox checked={confirmCorrect} onCheckedChange={(v) => setConfirmCorrect(!!v)} data-testid="onboarding-confirm" className="mt-0.5" />
        <span className="text-sm text-cass-slate">I confirm the information provided is complete and correct to the best of my knowledge.</span>
      </label>

      <div className="flex items-center gap-2 text-xs text-cass-slate">
        <ShieldCheck className="w-4 h-4 text-cass-teal" />Your information is transmitted securely and stored in confidence.
      </div>
    </div>
  );
}
